import { useEffect, useState } from 'react';
import { Settings, ResponsiveObject } from 'react-slick';
import dayjs from 'dayjs';
import { UiSlider, UiTitle } from 'znanie-uikit';

import { shuffleAndSliceArray } from 'utils/helpers';

import SpeakerAdvancedCard from 'components/common/SpeakerAdvancedCard';
import LayoutConfines from 'components/layout/LayoutConfines';

import { SpeakerType } from 'graphql/types';

import css from './SpeakersOnMain.module.scss';

interface IProps {
  speakers: SpeakerType[];
}

const getSettings = (): Settings => ({
  slidesToShow: 3,
  arrows: true,
  infinite: true,
  centerMode: true,
  variableWidth: true,
  responsive: [
    {
      breakpoint: 768,
      settings: {
        arrows: false,
      },
    },
  ] as ResponsiveObject[],
  nextArrow: <UiSlider.NextArrow customClass={css.arrow} />,
  prevArrow: <UiSlider.PrevArrow customClass={css.arrow} />,
});

type SpeakersOnMainCache = {
  dayOfYear: number;
  shuffledSpeakers: SpeakerType[];
};

export default function SpeakersOnMain({ speakers }: IProps) {
  const slicedSpeakersCount: number = 10;
  const [shuffledSpeakers, setShuffledSpeakers] = useState<SpeakerType[]>([]);
  const cachedKey: string = SpeakersOnMain.name;

  const getSpeakersCache = (key: string): SpeakersOnMainCache =>
    JSON.parse(localStorage.getItem(key) || '') as SpeakersOnMainCache;

  const prepareSpeakersCache = (
    dayOfYear: number,
    speakers: SpeakerType[]
  ): SpeakersOnMainCache => ({
    dayOfYear,
    shuffledSpeakers: shuffleAndSliceArray<SpeakerType>(
      speakers,
      slicedSpeakersCount
    ) as SpeakerType[],
  });

  const saveSpeakersToCache = (
    key: string,
    payload: SpeakersOnMainCache
  ): void => localStorage.setItem(cachedKey, JSON.stringify(payload));

  useEffect(() => {
    //Кеш спикеров зависит от данной переменной, если её изменять, сортировка спикеров будет обновляться
    const currentDayOfYear: number = dayjs().dayOfYear();

    if (!localStorage.getItem(cachedKey)) {
      saveSpeakersToCache(
        cachedKey,
        prepareSpeakersCache(currentDayOfYear, speakers) as SpeakersOnMainCache
      );
    } else {
      const { dayOfYear }: SpeakersOnMainCache = getSpeakersCache(cachedKey);
      if (dayOfYear !== currentDayOfYear) {
        saveSpeakersToCache(
          cachedKey,
          prepareSpeakersCache(
            currentDayOfYear,
            speakers
          ) as SpeakersOnMainCache
        );
      }
    }

    const { shuffledSpeakers }: SpeakersOnMainCache =
      getSpeakersCache(cachedKey);
    setShuffledSpeakers(shuffledSpeakers);
  }, []);

  return (
    <div className={css.block}>
      <LayoutConfines>
        <div className={css.wrapper}>
          <UiTitle size="big" as="h2">
            Спикеры
          </UiTitle>
        </div>
      </LayoutConfines>
      <div className={css.container}>
        <UiSlider {...getSettings()}>
          {shuffledSpeakers.map((s: SpeakerType, i) => (
            <SpeakerAdvancedCard
              className={css.card}
              key={i}
              fullName={`${s.firstName} ${s.lastName}`}
              photoUrl={(s.bigPhotoUrl ?? s.photoUrl) || ''}
              description={s.title || ''}
            />
          ))}
        </UiSlider>
      </div>
    </div>
  );
}
